'use client';

import type { ReactNode } from 'react';
import Button from '@ui/components/button/buttonUI';
import CloseButton from '@ui/components/button/buttonClose';
import { cn } from '@ngg/storefront-utils';
import type { Dictionary } from '@ts/dictionary';
import type { StockStatus } from '@ts/loopTypes/stockStatus';
import { convertValueToString } from '@utils/function';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import Dialog from '@ui/components/Dialog/Dialog';
import useElementDimensions from '@utils/hooks/useElementDimensions';
import Icon from '@ui/components/icon/icon';
import useScroll from '@utils/useScroll';
import useWindowSize from '@utils/useWindowSize';
import { useMenuStore } from '@app/_stores/menu.store';
import type { VariantAttribute } from '@ts/variant';
import {
  hasSoonBackInStockStatus,
  itemIsInStock,
  showStatus,
} from './utils/utils';
import { Heading } from '../Heading/Heading';
import Paragraph from '../Paragraph/Paragraph';
import { renderVariantPrice } from './utils/renderVariantPrice';

const selectableStockStatuses: StockStatus[] = [
  'DeliveryItem',
  'StockItem',
  'PendingSoldOut',
  'OutOfStock',
  'SoldOut',
];

function renderValue(
  value: string | number,
  attributes: Map<string, VariantAttribute[]>,
) {
  const attribute = attributes.get(String(value));
  if (!attribute) return value;
  return (
    <>
      {attribute[0].swatch && (
        <Image
          className="mr-2"
          src={attribute[0].swatch}
          alt={attribute[0].label}
          width={24}
          height={24}
        />
      )}
      <span className="text-xs">{attribute[0].label}</span>
    </>
  );
}

export default function VariantSelector({
  attribute,
  name,
  label,
  options,
  value,
  onChange,
  dictionary,
}: {
  attribute: Map<string, VariantAttribute[]>;
  name: string;
  label: string;
  options: [string, VariantAttribute[]][];
  value: string | number;
  onChange: (e: string) => void;
  dictionary: Dictionary;
  disabled?: boolean;
  indicateFromPrice?: boolean;
  renderSwatch?: ({
    disabled,
    name,
    imgUrl,
  }: {
    disabled: boolean;
    name: string;
    imgUrl: string;
  }) => ReactNode;
}) {
  const { topBarVisible, topBarHeight, setDrawerIsOpen } = useMenuStore();
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const { height: navbarHeight } = useElementDimensions('navbar', 'offset');
  const { height: campaignBarHeight = 0 } = useElementDimensions(
    'campaign-bar',
    'offset',
  );

  const { scrollPosition } = useScroll();
  const { isBreakpoint } = useWindowSize('md');
  const offset = useMemo(() => {
    return navbarHeight && scrollPosition > navbarHeight
      ? topBarHeight - (topBarVisible ? topBarHeight : 0)
      : 0;
  }, [navbarHeight, scrollPosition, topBarVisible, topBarHeight]);

  const partOfNavbarVisible = useMemo(() => {
    return navbarHeight
      ? Math.max(0, navbarHeight - campaignBarHeight - offset)
      : 0;
  }, [navbarHeight, campaignBarHeight, offset]);

  const handleSelection = (selection: string) => {
    onChange(selection);
    setDrawerOpen(false);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  useEffect(() => {
    setDrawerIsOpen(drawerOpen);
  }, [drawerOpen, setDrawerIsOpen]);

  const minHeight =
    (options?.length ? options.length * 81 : 0) +
    (isBreakpoint ? partOfNavbarVisible : 0);

  return (
    <>
      <Heading as="h3" className="mb-2 mt-4 block text-sm md:mt-6">
        {label}
      </Heading>
      <Button
        onClick={() => setDrawerOpen(!drawerOpen)}
        name={name}
        className={cn(
          'mt-0 h-[74px] w-full border border-borderColor p-4',
          !value && 'text-grey-300',
          value && 'border-black',
        )}>
        <span
          className="mr-auto flex items-center truncate"
          aria-label="Product choice selector">
          {value ? (
            renderValue(value, attribute)
          ) : (
            <Paragraph className="text-sm">
              {dictionary.chooseAttribute?.replace('%', label.toLowerCase())}
            </Paragraph>
          )}
        </span>
        <Icon name="rightArrow" size={16} color="black" />
      </Button>

      <Dialog
        dialogTransitionProps={{
          enter: 'ease-out duration-200',
          enterFrom: 'translate-x-full',
          enterTo: '-translate-x-0',
          leaveFrom: '-translate-x-0',
          leaveTo: 'translate-x-full',
        }}
        title={dictionary.chooseAttribute?.replace('%', label.toLowerCase())}
        open={drawerOpen}
        onClose={closeDrawer}
        CloseButton={() => CloseButton(closeDrawer, 'px-2 py-4 m-0')}
        classNames={{
          backdrop: 'bg-black/[.06] ',
          panel: cn([
            'py-0 m-0 pb-0',
            'fixed right-0 bottom-0',
            'bg-main',
            'md:max-w-[375px] xl:max-w-[456px] max-w-[326px]',
            'border-l border-borderColor',
            'rounded-none scrollbar-none z-40 flex flex-col h-full overflow-y-auto overflow-x-hidden',
          ]),
          title:
            'font-bold leading-4 tracking-0.02em text-base mt-4 mb-5 mx-auto md:mx-0',
          panelHeader:
            'flex w-full items-center justify-end md:justify-between px-4 pr-0 text-sm font-bold',
          container: 'border-l-1-borderColor',
        }}
        top={isBreakpoint ? partOfNavbarVisible : 0}>
        <div className="flex flex-col justify-start">
          {isBreakpoint && (
            <Button onClick={closeDrawer} className="gap-1 px-4 pb-5">
              <Icon name="leftArrow" size={16} color="black" />
              <Paragraph className="text-xs" style={{ textAlign: 'center' }}>
                {dictionary?.back}
              </Paragraph>
            </Button>
          )}
          <ul
            style={{ minHeight: minHeight }}
            className={cn(['overflow-y-scroll'])}>
            {options.map(([val, variantAttributes], index) => {
              const displayStatus =
                itemIsInStock(variantAttributes) ||
                hasSoonBackInStockStatus(variantAttributes);
              const attributeValue = convertValueToString(val);
              const itemDisabled = !variantAttributes.some((variantAttribute) =>
                selectableStockStatuses.includes(variantAttribute.stockStatus),
              );
              const { swatch, code, label } = variantAttributes[0];

              return (
                <li
                  onClick={() => {
                    handleSelection(attributeValue);
                  }}
                  key={`${value}-${index.toString()}`}
                  className={cn([
                    'flex min-h-[81px] w-full cursor-pointer items-center border-t p-4 text-xs last:border-b hover:bg-beige',
                    itemDisabled && 'pointer-events-none text-black/40',
                    attributeValue === value && 'bg-beige',
                  ])}>
                  {swatch && (
                    <Image
                      className="mr-2"
                      src={swatch}
                      alt={code}
                      width={24}
                      height={24}
                    />
                  )}

                  <Paragraph className="mr-auto truncate text-sm">
                    {label}
                  </Paragraph>

                  <div className="flex flex-col items-end justify-center">
                    {renderVariantPrice(variantAttributes, dictionary)}
                    {displayStatus && (
                      <span className="mt-2 flex items-center justify-center gap-1 text-xs">
                        <Icon name="dot" size={6} color="#009A44" />
                        {showStatus(variantAttributes, dictionary)}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </Dialog>
    </>
  );
}
